import { Injectable } from '@angular/core';
import type { Cell, Row, Workbook, Worksheet } from 'exceljs';

import { ExportWorkbookStatusService } from '../../../shared/services/export-workbook-status.service';
import {
  REPORT_ITEM_COLUMNS,
  VARIABLE_COLUMN_MAX_LENGTH,
  normalizeReportItemHeader
} from '../../../shared/utils/report-item-columns';

/** A value read from one sheet cell, in the shape the row's form control expects. */
export type ReportItemImportValue = string | number | Date;

export interface ReportItemsImportRow {
  /** 1-based worksheet row number, so the confirmation dialog can point back at the sheet. */
  sheetRow: number;
  lot: string;
  runId: string;
  assay: string;
  /** Every column the sheet supplied for this row, keyed by form control name. */
  values: Record<string, ReportItemImportValue>;
}

export interface ReportItemsImportSkip {
  sheetRow: number;
  reason: string;
}

export interface ReportItemsImportParse {
  fileName: string;
  sheetName: string;
  /** Rows that can be applied to the table. */
  rows: ReportItemsImportRow[];
  /** Rows that named a kit lot, instrument and assay the study does not have. */
  skipped: ReportItemsImportSkip[];
  /** Rows missing any of Kit Lot, Instrument ID or Assay Name. */
  incompleteCount: number;
  /** Sheet headers that matched no column of the table. */
  ignoredHeaders: string[];
}

/** What the study allows, so the parser can reject values no dropdown could hold. */
export interface ReportItemsImportContext {
  kitLots: string[];
  instrumentIds: string[];
  assayNames: string[];
  beadRegions: number[];
  /** Form keys of the panel's variable bulk-material columns. */
  variableColumns: string[];
  /**
   * Header shown for each variable column, keyed by form key. The template writes these as its
   * headers, and a bulk-material description does not always reduce to its own camelCase key, so
   * they are registered alongside it.
   */
  variableColumnLabels?: Record<string, string>;
  /** Result type the run type forces on every row; when set, a sheet value is ignored. */
  forcedResultType: string | null;
}

export interface ReportItemsTemplatePayload {
  fileName: string;
  headers: string[];
  rows: (string | number | Date | null)[][];
}

const RESULT_TYPES = ['Instrument', 'Calculated'];
const HEADER_SEARCH_DEPTH = 25;
const PREFERRED_SHEET_KEY = 'reportitems';

@Injectable({ providedIn: 'root' })
export class ReportItemsExcelService {
  constructor(private readonly exportWorkbookStatus: ExportWorkbookStatusService) {}

  /**
   * Reads a Report Items worksheet into validated rows. Columns are located by header name, so the
   * sheet's column order does not matter and extra columns are simply reported as ignored.
   *
   * @throws Error with a message fit for display when the file holds no recognizable header row.
   */
  async parseFile(file: File, context: ReportItemsImportContext): Promise<ReportItemsImportParse> {
    const workbook = await this.loadWorkbook(file);
    const located = this.locateSheet(workbook, context);
    if (!located) {
      throw new Error(
        'No Report Items header row was found. The sheet needs a row of column headers including Kit Lot, Instrument ID and Assay Name.'
      );
    }

    const { sheet, headerRow, columnsByNumber, ignoredHeaders } = located;
    const rows: ReportItemsImportRow[] = [];
    const skipped: ReportItemsImportSkip[] = [];
    let incompleteCount = 0;

    sheet.eachRow({ includeEmpty: false }, (row: Row, rowNumber: number) => {
      if (rowNumber <= headerRow) {
        return;
      }

      const raw = this.readRow(row, columnsByNumber);
      const lotText = this.asText(raw['lot']);
      const runIdText = this.asText(raw['runId']);
      const assayText = this.asText(raw['assay']);

      if (!lotText || !runIdText || !assayText) {
        // Only rows naming all three are meant to populate the table; blank filler and any
        // trailing notes rows in the sheet land here.
        if (this.hasAnyValue(raw)) {
          incompleteCount += 1;
        }
        return;
      }

      const lot = this.matchOption(lotText, context.kitLots);
      const runId = this.matchOption(runIdText, context.instrumentIds);
      const assay = this.matchCanonical(assayText, context.assayNames);
      const unknown = [
        lot ? '' : `Kit Lot "${lotText}"`,
        runId ? '' : `Instrument ID "${runIdText}"`,
        assay ? '' : `Assay Name "${assayText}"`
      ].filter(Boolean);

      if (unknown.length) {
        skipped.push({ sheetRow: rowNumber, reason: `${unknown.join(', ')} not in this study` });
        return;
      }

      rows.push({
        sheetRow: rowNumber,
        lot,
        runId,
        assay,
        values: this.buildRowValues(raw, { lot, runId, assay }, context)
      });
    });

    return {
      fileName: file.name,
      sheetName: sheet.name,
      rows,
      skipped,
      incompleteCount,
      ignoredHeaders
    };
  }

  /** Downloads the current table as the workbook the importer expects back. */
  async downloadTemplate(payload: ReportItemsTemplatePayload): Promise<void> {
    this.exportWorkbookStatus.start();
    try {
      const workbook = await this.createWorkbook();
      const sheet = workbook.addWorksheet('Report Items');

      const header = sheet.addRow(payload.headers);
      header.font = { bold: true };
      payload.rows.forEach(row => {
        const added = sheet.addRow(row.map(cell => (cell instanceof Date ? this.toUtcMidnight(cell) : cell)));
        row.forEach((cell, index) => {
          if (cell instanceof Date) {
            // Without a format Excel shows the underlying serial number.
            added.getCell(index + 1).numFmt = 'm/d/yyyy';
          }
        });
      });
      sheet.views = [{ state: 'frozen', ySplit: 1 }];
      payload.headers.forEach((label, index) => {
        const widest = payload.rows.reduce(
          (longest, row) => Math.max(longest, this.asText(row[index] ?? '').length),
          label.length
        );
        sheet.getColumn(index + 1).width = Math.min(40, Math.max(12, widest + 2));
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      this.triggerDownload(blob, payload.fileName);
    } finally {
      this.exportWorkbookStatus.stop();
    }
  }

  private async loadWorkbook(file: File): Promise<Workbook> {
    const workbook = await this.createWorkbook();
    const buffer = await file.arrayBuffer();
    // ExcelJS types `load` against Node's Buffer; in the browser it reads an ArrayBuffer.
    await (workbook.xlsx as unknown as { load(data: ArrayBuffer): Promise<unknown> }).load(buffer);
    return workbook;
  }

  private async createWorkbook(): Promise<Workbook> {
    const ExcelJSImport = await import('exceljs');
    const WorkbookCtor =
      (ExcelJSImport as { Workbook?: new () => Workbook }).Workbook ??
      (ExcelJSImport as { default?: { Workbook?: new () => Workbook } }).default?.Workbook;
    if (!WorkbookCtor) {
      throw new Error('ExcelJS Workbook constructor not found.');
    }
    return new WorkbookCtor();
  }

  /**
   * Picks the worksheet to read and the row its headers sit on. A sheet named "Report Items" wins;
   * otherwise the first sheet with a usable header row does, which lets a saved report with a title
   * block above the table still import.
   */
  private locateSheet(
    workbook: Workbook,
    context: ReportItemsImportContext
  ): { sheet: Worksheet; headerRow: number; columnsByNumber: Map<number, string>; ignoredHeaders: string[] } | null {
    const headerLookup = this.buildHeaderLookup(context);
    const sheets = [...(workbook.worksheets ?? [])].sort((a, b) => {
      const aPreferred = normalizeReportItemHeader(a.name) === PREFERRED_SHEET_KEY ? 0 : 1;
      const bPreferred = normalizeReportItemHeader(b.name) === PREFERRED_SHEET_KEY ? 0 : 1;
      return aPreferred - bPreferred;
    });

    for (const sheet of sheets) {
      const depth = Math.min(sheet.rowCount ?? 0, HEADER_SEARCH_DEPTH);
      for (let rowNumber = 1; rowNumber <= depth; rowNumber++) {
        const mapped = this.mapHeaderRow(sheet.getRow(rowNumber), headerLookup);
        if (!mapped) {
          continue;
        }
        return { sheet, headerRow: rowNumber, ...mapped };
      }
    }

    return null;
  }

  /**
   * Maps one candidate row's cells onto form keys, or returns null when the row is not the header
   * row. A header row has to carry all three of the columns that identify a report item, so a data
   * row that happens to hold text cannot be mistaken for one.
   */
  private mapHeaderRow(
    row: Row,
    headerLookup: Map<string, string>
  ): { columnsByNumber: Map<number, string>; ignoredHeaders: string[] } | null {
    const columnsByNumber = new Map<number, string>();
    const ignoredHeaders: string[] = [];

    row.eachCell({ includeEmpty: false }, (cell: Cell, columnNumber: number) => {
      const text = this.cellText(cell);
      if (!text) {
        return;
      }
      const formKey = headerLookup.get(normalizeReportItemHeader(text));
      // First header wins, so a sheet with the same column twice does not read the empty copy.
      if (formKey && !this.hasFormKey(columnsByNumber, formKey)) {
        columnsByNumber.set(columnNumber, formKey);
        return;
      }
      if (!formKey) {
        ignoredHeaders.push(text);
      }
    });

    const identifiers = ['lot', 'runId', 'assay'];
    const found = new Set(columnsByNumber.values());
    if (identifiers.some(key => !found.has(key))) {
      return null;
    }

    return { columnsByNumber, ignoredHeaders };
  }

  private hasFormKey(columnsByNumber: Map<number, string>, formKey: string): boolean {
    for (const value of columnsByNumber.values()) {
      if (value === formKey) {
        return true;
      }
    }
    return false;
  }

  /** Every header spelling the importer accepts, mapped to the form control it fills. */
  private buildHeaderLookup(context: ReportItemsImportContext): Map<string, string> {
    const lookup = new Map<string, string>();
    const add = (header: string, formKey: string) => {
      const key = normalizeReportItemHeader(header);
      if (key && !lookup.has(key)) {
        lookup.set(key, formKey);
      }
    };

    REPORT_ITEM_COLUMNS.forEach(column => {
      add(column.label, column.formKey);
      add(column.columnKey, column.formKey);
      add(column.formKey, column.formKey);
      column.aliases?.forEach(alias => add(alias, column.formKey));
    });
    // A variable column's camelCase key usually normalizes to the same key as its
    // "<material> LN" label; the label is registered too for the descriptions where it does not.
    context.variableColumns.forEach(formKey => {
      add(formKey, formKey);
      const label = context.variableColumnLabels?.[formKey];
      if (label) {
        add(label, formKey);
      }
    });

    return lookup;
  }

  private readRow(row: Row, columnsByNumber: Map<number, string>): Record<string, Cell> {
    const raw: Record<string, Cell> = {};
    columnsByNumber.forEach((formKey, columnNumber) => {
      raw[formKey] = row.getCell(columnNumber);
    });
    return raw;
  }

  private hasAnyValue(raw: Record<string, Cell>): boolean {
    return Object.values(raw).some(cell => !!this.cellText(cell));
  }

  /** Converts the recognized cells of one row into form values, dropping what cannot be used. */
  private buildRowValues(
    raw: Record<string, Cell>,
    identity: { lot: string; runId: string; assay: string },
    context: ReportItemsImportContext
  ): Record<string, ReportItemImportValue> {
    const values: Record<string, ReportItemImportValue> = { ...identity };

    // The run type forces a result type on every row on load, so only read the sheet's when it does not.
    if (!context.forcedResultType && 'resultType' in raw) {
      const resultType = this.matchOption(this.cellText(raw['resultType']), RESULT_TYPES);
      if (resultType) {
        values['resultType'] = resultType;
      }
    }

    // A region the panel does not have is dropped rather than rejected: selecting the assay fills
    // the region in on its own.
    if ('region' in raw) {
      const region = Number(this.cellText(raw['region']));
      if (Number.isFinite(region) && context.beadRegions.includes(region)) {
        values['region'] = region;
      }
    }

    if ('beadCouplingDate' in raw) {
      const date = this.cellDate(raw['beadCouplingDate']);
      if (date) {
        values['beadCouplingDate'] = date;
      }
    }

    REPORT_ITEM_COLUMNS.filter(column => column.maxLength).forEach(column => {
      const cell = raw[column.formKey];
      if (!cell) {
        return;
      }
      values[column.formKey] = this.cellText(cell).slice(0, column.maxLength);
    });

    context.variableColumns.forEach(formKey => {
      const cell = raw[formKey];
      if (cell) {
        values[formKey] = this.cellText(cell).slice(0, VARIABLE_COLUMN_MAX_LENGTH);
      }
    });

    return values;
  }

  /** Case- and whitespace-insensitive, but punctuation still distinguishes one lot from another. */
  private matchOption(value: string, options: string[]): string {
    const target = value.trim().toLowerCase();
    if (!target) {
      return '';
    }
    return options.find(option => String(option ?? '').trim().toLowerCase() === target) ?? '';
  }

  /** Assay names are compared the way the rest of the app compares them — punctuation and all stripped. */
  private matchCanonical(value: string, options: string[]): string {
    const target = normalizeReportItemHeader(value);
    if (!target) {
      return '';
    }
    return options.find(option => normalizeReportItemHeader(option) === target) ?? '';
  }

  private cellText(cell: Cell | undefined): string {
    return this.asText(cell?.value);
  }

  /** ExcelJS hands back rich text, hyperlinks, formula results and errors as objects. */
  private asText(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'string') {
      return value.trim();
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (value instanceof Date) {
      return this.formatDate(value);
    }
    const record = value as Record<string, unknown>;
    if (Array.isArray(record['richText'])) {
      return (record['richText'] as { text?: string }[]).map(part => part.text ?? '').join('').trim();
    }
    if (typeof record['text'] === 'string') {
      return record['text'].trim();
    }
    if ('result' in record) {
      const result = record['result'];
      return result && typeof result === 'object' && !(result instanceof Date) ? '' : this.asText(result);
    }
    return '';
  }

  /**
   * A date cell, or a date typed as text. A bare number is left alone rather than read as an Excel
   * serial — ExcelJS already returns a Date for any date-formatted cell.
   *
   * Every path lands on local midnight of the calendar day the sheet shows. ExcelJS returns a date
   * cell as midnight UTC and JS parses a bare ISO date the same way, so reading either with the
   * local getters the app formats dates with would move the day back in any timezone behind UTC.
   */
  private cellDate(cell: Cell | undefined): Date | null {
    const value = cell?.value;
    if (value instanceof Date) {
      return this.toLocalDate(value.getUTCFullYear(), value.getUTCMonth() + 1, value.getUTCDate());
    }

    const text = this.cellText(cell);
    if (!text) {
      return null;
    }

    const isoParts = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(text);
    if (isoParts) {
      return this.toLocalDate(Number(isoParts[1]), Number(isoParts[2]), Number(isoParts[3]));
    }

    // Anything else — "5/1/2026" and the like — JS already parses as a local date.
    const parsed = new Date(text);
    if (Number.isNaN(parsed.valueOf())) {
      return null;
    }
    return this.toLocalDate(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate());
  }

  private toLocalDate(year: number, month: number, day: number): Date | null {
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.valueOf()) ? null : date;
  }

  /** Written back as midnight UTC, so the day survives the round trip through a sheet. */
  private toUtcMidnight(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  }

  private formatDate(date: Date): string {
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  }

  private triggerDownload(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
